[<RequireQualifiedAccess>]
module Mousetrap

open Fable.Core
open Fable.Core.JsInterop
open Browser.Types
open Browser

type [<RequireQualifiedAccess; StringEnum>] EventType =
    | Keypress
    | Keydown
    | Keyup

type Handler = KeyboardEvent -> string -> unit
type HandlerWithCancellation = KeyboardEvent -> string -> bool

///When given an event, element and command, return true to stop the handler from executing, or false to allow it to execute.
type StopCallback = KeyboardEvent -> HTMLElement -> string -> bool

let [<Literal>] className = "mousetrap"

type IMousetrap =
    ///This function is determines whether or not a handler should fire from a given element, such as a form field.
    abstract stopCallback: StopCallback with get, set

    ///Invoke the default stop callback handler for this instance, useful for falling back from a custom handler
    [<Emit("(Object.getPrototypeOf($0).stopCallback).call($0, $1...)")>]
    abstract invokeDefaultStopCallback: KeyboardEvent -> HTMLElement -> string -> bool

    ///Bind a specified keyboard command to a callback method.
    abstract bind: command: string * handler: Handler * ?eventType: EventType -> unit

    ///Bind a specified keyboard command to a callback method.
    abstract bind: command: string * handler: HandlerWithCancellation * ?eventType: EventType -> unit

    ///Bind multiple keyboard commands to a callback method.
    abstract bind: commands: string array * handler: Handler * ?eventType: EventType -> unit

    ///Bind multiple keyboard commands to a callback method.
    abstract bind: commands: string array * handler: HandlerWithCancellation * ?eventType: EventType -> unit

    ///Unbind a keyboard command. If the binding was made with an event type then this type must also be provided.
    abstract unbind: command: string * ?eventType: EventType -> unit

    ///Unbind a multiple keyboard commands. If the bindings were made with an event type then this type must also be provided.
    abstract unbind: commands: string array * ?eventType: EventType -> unit

    ///Trigger the handler for the given command. Note that this does not raise an event in the browser.
    abstract trigger: command: string * ?eventType: EventType -> unit

    //Remove all bindings.
    abstract reset: unit -> unit

type internal IMousetrapModule =
    inherit IMousetrap

    [<Emit("$0($1)")>]
    abstract element: Node -> IMousetrap

    [<Emit("$0($1)")>]
    abstract window: Window -> IMousetrap

let private mousetrapModule = importDefault<IMousetrapModule> "mousetrap"

///Create bindings for the current document
let document: IMousetrap = mousetrapModule.element(window.document)

///Create bindings for a specific element
let forElement (element: HTMLElement) : IMousetrap  = mousetrapModule.element element
